import { useEffect, useState } from "react";
import { TitleHeader } from "../../common/components/TitleHeader/TitleHeader";
import { ColumnItem } from "../../common/models/table";
import { ActionsKey } from "../../common/models/Actions";
import { CustomDrawer } from "../../common/components/Drawer/CustomDrawer";
import { MachineForm } from "./components/machine/MachineForm";
import { MaterialService } from "./core/_request";
import { HttpStatusCode } from "axios";
import {
  useMachineMaterialListState,
  useMachineMaterialStore,
} from "./store/MachineStore";
import { Loader } from "../../common/components/Loader/Loader";
import { LFTableV2 } from "../../common/components/LFTable/LFTableV2";
import {
  useQueryMachineMaterialFromSelect,
  useQueryMachineMaterialStateSelect,
  useQueryMachineMaterialTypeSelect,
} from "./core/_hooks";
import { LFTableV3 } from "../../common/components/LFTable/LFTableV3";
import { Tag } from "antd";
import { getStateTagData } from "../../utilities/StateTag";

export const MachinePage = () => {
  const { data: stateOptions, isLoading: isLoadingState } =
    useQueryMachineMaterialStateSelect({}, false);
  const { data: fromOptions, isLoading: isLoadingFrom } =
    useQueryMachineMaterialFromSelect({}, false);
  const { data: typeOptions, isLoading: isLoadingType } =
    useQueryMachineMaterialTypeSelect({}, false);

  const {
    setMachineMaterialList,
    machineMaterialList,
    actioned,
    totalItems,
    setActioned,
  } = useMachineMaterialStore();
  const { pageState, setPageState, resetPageState } =
    useMachineMaterialListState();

  const [isShowForm, setIsShowForm] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const cols: ColumnItem[] = [
    {
      key: "reference",
      title: "Référence",
      dataIndex: "reference",
      show: true,
      isCanSort: true,
      searchType: "text",
    },
    {
      key: "capacity",
      title: "Capacité (T) ",
      dataIndex: "capacity",
      show: true,
    },
    {
      key: "materialState",
      title: "Etat",
      dataIndex: "materialState",
      show: true,
      nestedKey: "materialState",
      searchType: "select",
      searchOptions: stateOptions,
      isLoading: isLoadingState,
      render: (state: any) => {
        const { color } = getStateTagData(state?.id);
        return (
          <div className="h-100 w-100 d-flex align-items-center justify-content-center">
            <Tag color={color}>{state?.name}</Tag>
          </div>
        );
      },
    },
    {
      key: "materialFrom",
      title: "Propriétaire",
      dataIndex: "materialFrom",
      show: true,
      searchType: "select",
      nestedKey: "materialFrom",
      searchOptions: fromOptions,
      isLoading: isLoadingFrom,
      render: (state: any) => {
        return (
          <div className="h-100 w-100 d-flex align-items-center justify-content-center">
            <span>{state?.name}</span>
          </div>
        );
      },
    },
    {
      key: "driver",
      title: "Conducteur",
      dataIndex: "driver",
      show: true,
      nestedKey: "driver",
      isLoading: isLoadingFrom,
      render: (driver: any, record: any) => {
        const name = driver?.fullname ?? record?.comamaDriverName ?? "-";
    
        return (
          <div className="h-100 w-100 d-flex align-items-center justify-content-center">
            <span>{name}</span>
          </div>
        );
      },
    },
    {
      key: "machineMaterialType",
      title: "Type",
      dataIndex: "machineMaterialType",
      show: true,
      searchType: "select",
      nestedKey: "machineMaterialType",
      searchOptions: typeOptions,
      isLoading: isLoadingType,
      render: (state: any) => {
        return (
          <div className="h-100 w-100 d-flex align-items-center justify-content-center">
            <span>{state?.name}</span>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    fetchAllMaterialMachine(pageState);
  }, [pageState]);

  useEffect(() => {
    if (!isShowForm) {
      setActioned({ action: "no", data: null });
    }
  }, [isShowForm]);

  const onActionClick = (item: any, action: ActionsKey) => {
    if (action === "delete") {
      onDeleteHandler(item?.id);
    } else {
      getOne(item?.id, action);
    }
  };

  const onShowAddHandler = () => {
    setIsShowForm((prev: boolean) => !prev);
  };

  const onClickOnAdd = () => {
    onShowAddHandler();
  };

  const getOne = async (id: number, action: any) => {
    try {
      setIsLoading(true);
      const res = await MaterialService.getOneMachineMaterial(id);
      if (res?.status === HttpStatusCode.Ok) {
        setActioned({
          data: {
            ...res?.data,
          },
          action,
        });
      }
      onShowAddHandler();
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onDeleteHandler = async (id: number) => {
    try {
      setIsLoading(true);
      const res = await MaterialService.deleteMachineMaterial(id);
      if (res?.status === HttpStatusCode.NoContent) {
        await fetchAllMaterialMachine();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllMaterialMachine = async (params?: any) => {
    try {
      setIsLoading(true);
      const res = await MaterialService.getMachineMaterial(params);
      if (res?.status === HttpStatusCode.Ok) {
        setMachineMaterialList(res?.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onFinishAction = () => {
    resetPageState();
    onShowAddHandler();
  };

  const handleSetParams = (params: any) => {
    setPageState({
      ...params,
    });
  };

  return (
    <div className="">
      <Loader isLoading={isLoading} />
      <TitleHeader
        title="Matériels roulants"
        isSHowBack
        isEnableAdd
        onClickOnAdd={onClickOnAdd}
      />
      <div className="mt-4">
        {/* Table section  */}
        <div className="">
          <LFTableV3
            columns={cols}
            data={machineMaterialList}
            params={pageState}
            onAction={onActionClick}
            isCanEdit={() => true}
            isCanDelete={() => true}
            onSearch={handleSetParams}
            onSort={handleSetParams}
            onResetSearch={resetPageState}
            pagination={{
              defaultCurrentPage: pageState.page,
              itemsPerPage: pageState?.itemsPerPage,
              totalItems: totalItems,
            }}
            onChangePage={handleSetParams}
          />
        </div>
      </div>

      {/* Drawer section  */}
      {isShowForm ? (
        <CustomDrawer
          isOpen={isShowForm}
          onClose={onShowAddHandler}
          title={actioned?.action === "update" ? "Modification" : "Nouveau"}
          width={"75%"}
        >
          <MachineForm
            onFinish={onFinishAction}
            {...(actioned?.action === "update"
              ? {
                  initialValue: actioned?.data,
                }
              : {})}
          />
        </CustomDrawer>
      ) : (
        ""
      )}
    </div>
  );
};
